import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FluidHoverGroup } from '@/components/ui/fluid-hover-group';
import { FluidHoverLabelRow } from '@/components/ui/fluid-hover-row';

export interface AccessChecklistItem {
    id: string;
    label: string;
    leading?: React.ReactNode;
}

interface AccessChecklistProps {
    title: string;
    items: AccessChecklistItem[];
    selectedIds: string[];
    onSelectedIdsChange: React.Dispatch<React.SetStateAction<string[]>>;
    emptyText: string;
    saving: boolean;
    onCancel: () => void;
    onSave: (ids: string[]) => void;
}

export const AccessChecklist: React.FC<AccessChecklistProps> = ({
    title,
    items,
    selectedIds,
    onSelectedIdsChange,
    emptyText,
    saving,
    onCancel,
    onSave
}) => {
    const toggle = (id: string, checked: boolean) =>
        onSelectedIdsChange((previous) =>
            checked
                ? [...previous, id]
                : previous.filter((selected) => selected !== id)
        );

    return (
        <>
            <p className="px-4 py-3 border-border border-b text-(length:--fs-subtitle) text-foreground leading-(--lh-subtitle)">
                {title}
            </p>
            {items.length === 0 ? (
                <p className="px-4 py-6 text-(length:--fs-caption) text-muted-foreground text-center leading-(--lh-caption)">
                    {emptyText}
                </p>
            ) : (
                <FluidHoverGroup className="space-y-0.5 p-1.5 max-h-60 overflow-y-auto scrollbar-hidden">
                    {items.map((item) => {
                        const checked = selectedIds.includes(item.id);
                        return (
                            <FluidHoverLabelRow
                                key={item.id}
                                className="group/row flex items-center gap-3 px-2.5 py-2 rounded-lg"
                            >
                                <Checkbox
                                    variant="fluid"
                                    checked={checked}
                                    onCheckedChange={(value) =>
                                        toggle(item.id, value === true)
                                    }
                                />
                                {item.leading}
                                <span className="grid flex-1 min-w-0 text-(length:--fs-subtitle) leading-(--lh-subtitle)">
                                    <span
                                        aria-hidden
                                        className="invisible col-start-1 row-start-1 font-medium truncate"
                                    >
                                        {item.label}
                                    </span>
                                    <span
                                        className={cn(
                                            'col-start-1 row-start-1 truncate transition-colors duration-80',
                                            checked
                                                ? 'font-medium text-foreground'
                                                : 'text-muted-foreground group-hover/row:text-foreground'
                                        )}
                                    >
                                        {item.label}
                                    </span>
                                </span>
                            </FluidHoverLabelRow>
                        );
                    })}
                </FluidHoverGroup>
            )}
            <div className="flex gap-2 p-3 border-border border-t">
                <Button variant="tertiary" className="flex-1" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    className="flex-1"
                    disabled={saving}
                    onClick={() => onSave(selectedIds)}
                >
                    {saving ? <Loader2 className="animate-spin" /> : 'Save'}
                </Button>
            </div>
        </>
    );
};
